import { Injectable } from '@angular/core';
import { centsToDollars, dollarsToCents } from '@core/util/money';
import { Business } from '@models/businesses';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_190_CALCULATION_WS_Mapper extends BaseFormMapper<ON_GST_190_CALCULATION_WS> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_190_CALCULATION_WS,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_190_CALCULATION_WS);

		if (
			!_formData?.applicationType ||
			_formData?.applicationType?.length === 0
		) {
			_formData.applicationType = '0';
		}

		if (
			!_formData?.calculationType ||
			_formData?.calculationType?.length === 0
		) {
			_formData.calculationType = '0';
		}

		if (
			(!_formData?.rebateType1AOr2GSTPaid ||
				_formData?.rebateType1AOr2GSTPaid?.length === 0) &&
			adjustments?.salePriceCalculated?.hstFed
		) {
			_formData.rebateType1AOr2GSTPaid = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.hstFed,
			);
		}

		if (
			(!_formData?.rebateType1AOr2PurchasePrice ||
				_formData?.rebateType1AOr2PurchasePrice?.length === 0) &&
			adjustments?.salePriceCalculated?.nsp
		) {
			_formData.rebateType1AOr2PurchasePrice = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.nsp,
			);
		}

		if (
			(!_formData?.rebateType1AOr2AmountOne ||
				_formData?.rebateType1AOr2AmountOne?.length === 0) &&
			adjustments?.salePriceCalculated?.hstFed
		) {
			_formData.rebateType1AOr2AmountOne = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.hstFed,
			);
		}

		if (
			(!_formData?.rebateType1AOr2CalculationOne ||
				_formData?.rebateType1AOr2CalculationOne?.length === 0) &&
			_formData.rebateType1AOr2AmountOne
		) {
			let total =
				this.formValueToCents(_formData.rebateType1AOr2AmountOne) * 0.36;
			if (total > 630000) {
				total = 630000;
			}
			_formData.rebateType1AOr2CalculationOne = this.toFormDollarPrice(total);
		}

		if (
			(!_formData?.rebateType1AOr2AmountFour ||
				_formData?.rebateType1AOr2AmountFour?.length === 0) &&
			adjustments?.salePriceCalculated?.nsp
		) {
			_formData.rebateType1AOr2AmountFour = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.nsp,
			);
		}

		if (
			(!_formData?.rebateType1AOr2AmountFive ||
				_formData?.rebateType1AOr2AmountFive?.length === 0) &&
			_formData?.rebateType1AOr2CalculationOne
		) {
			_formData.rebateType1AOr2AmountFive =
				_formData?.rebateType1AOr2CalculationOne;
		}

		if (
			(!_formData?.rebateType1AOr2CalculationTwo ||
				_formData?.rebateType1AOr2CalculationTwo?.length === 0) &&
			_formData?.rebateType1AOr2AmountFour &&
			_formData?.rebateType1AOr2AmountFive
		) {
			const total =
				(this.formValueToCents(_formData?.rebateType1AOr2AmountFour) *
					this.formValueToCents(_formData?.rebateType1AOr2AmountFive)) /
				10000000;

			if (total > 45000000) {
				_formData.rebateType1AOr2CalculationTwo =
					this.toFormDollarPrice('0.00');
			} else if (total > 35000000) {
				_formData.rebateType1AOr2CalculationTwo = this.toFormDollarPrice(
					(45000000 -
						this.formValueToCents(_formData?.rebateType1AOr2AmountFour)) *
						this.formValueToCents(_formData?.rebateType1AOr2AmountFive),
				);
			} else {
				_formData.rebateType1AOr2CalculationTwo =
					_formData?.rebateType1AOr2AmountFive;
			}
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_190_CALCULATION_WS> {
		return {};
	}
}

const Mappings: ON_GST_190_CALCULATION_WS = {
	applicationType:
		'form1[0].Page1[0].WhatToFill[0].ApplicationType[0].ApplicationType[0]',
	calculationType:
		'form1[0].Page1[0].WhatToFill[0].ApplicationType[1].CalculType[0]',
	rebateType1AOr2GSTPaid:
		'form1[0].Page1[0].Section1[0].Line1[0].Line1Amount[0].Amount[0]',
	rebateType1AOr2PurchasePrice:
		'form1[0].Page2[0].Section1_Continued[0].Line2[0].Line2Amount[0].Amount[0]',
	rebateType1AOr2AmountOne:
		'form1[0].Page2[0].Section1_Continued[0].Line3[0].Line3_Text[0].Calcul1[0].Amount[0].Amount[0]',
	rebateType1AOr2AmountTwo:
		'form1[0].Page2[0].Section1_Continued[0].Line3[0].Line3_Text[0].Calcul2[0].Amount[0].Amount[0]',
	rebateType1AOr2AmountThee:
		'form1[0].Page2[0].Section1_Continued[0].Line3[0].Line3_Text[0].Calcul3[0].Amount[0].Amount[0]',
	rebateType1AOr2CalculationOne:
		'form1[0].Page2[0].Section1_Continued[0].Line3[0].Line3Amount[0].Amount[0]',
	rebateType1AOr2AmountFour:
		'form1[0].Page2[0].Section1_Continued[0].Line4[0].Line4_Text[0].Calcul3[0].Amount1[0].Amount[0]',
	rebateType1AOr2AmountFive:
		'form1[0].Page2[0].Section1_Continued[0].Line4[0].Line4_Text[0].Calcul3[0].Amount2[0].Amount[0]',
	rebateType1AOr2CalculationTwo:
		'form1[0].Page2[0].Section1_Continued[0].Line4[0].Line4Amount[0].Amount[0]',
	rebateType1BOr5TotalPurchasePrice:
		'form1[0].Page2[0].Section2[0].Line5[0].Line5Amount[0].Amount[0]',
	rebateType1BOr5FairMarketValue:
		'form1[0].Page2[0].Section2[0].Line6[0].Line6Amount[0].Amount[0]',
	rebateType1BOr5AmountOne:
		'form1[0].Page3[0].Section2_Continued[0].Line7[0].Line7Text[0].Calcul3[0].Amount1[0].Amount[0]',
	rebateType1BOr5RebateRate:
		'form1[0].Page3[0].Section2_Continued[0].Line7[0].Line7Text[0].Calcul3[0].Amount2[0].Percent[0]',
	rebateType1BOr5CalculationOne:
		'form1[0].Page3[0].Section2_Continued[0].Line7[0].Line7Amount[0].Amount[0]',
	rebateType1BOr5AmountTwo:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul1[0].Calcul[0].Amount1[0].Amount[0]',
	rebateType1BOr5AmountTwoCalculation:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul1[0].Calcul[0].Amount2[0].Amount[0]',
	rebateType1BOr5AmountThree:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul2[0].Calcul[0].Amount1[0].Amount[0]',
	rebateType1BOr5AmountThreeCalculation:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul2[0].Calcul[0].Amount2[0].Amount[0]',
	rebateType1BOr5AmountFour:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul3[0].Calcul[0].Amount1[0].Amount[0]',
	rebateType1BOr5AmountFourCalculation:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul3[0].Calcul[0].Amount2[0].Amount[0]',
	rebateType1BOr5AmountFive:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul4[0].Calcul[0].Amount1[0].Amount[0]',
	rebateType1BOr5AmountFiveCalculation:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8_Text[0].Calcul4[0].Calcul[0].Amount2[0].Amount[0]',
	rebateType1BOr5CalculationTwo:
		'form1[0].Page4[0].Section2_Continued[0].Line8[0].Line8Amount[0].Amount[0]',
	rebateType3CalculationOneAmount:
		'form1[0].Page5[0].Section3[0].Line10[0].Line10_Text[0].Calcul1[0].Amount[0].Amount[0]',
	rebateType3CalculationOneRebateRate:
		'form1[0].Page5[0].Section3[0].Line10[0].Line10_Text[0].Calcul1[0].Percent[0].Percent[0]',
	rebateType3CalculationOneTotal:
		'form1[0].Page5[0].Section3[0].Line9[0].Line9Amount[0].Amount[0]',
	rebateType3CalculationTwoTotal:
		'form1[0].Page5[0].Section3[0].Line10[0].Line10Amount[0].Amount[0]',
	rebateApplicationCalculationOneAmount:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul1[0].Calcul[0].Amount1[0].Amount[0]',
	rebateApplicationCalculationOneTotal:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul1[0].Calcul[0].Amount2[0].Amount[0]',
	rebateApplicationCalculationTwoAmount:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul2[0].Calcul[0].Amount1[0].Amount[0]',
	rebateApplicationCalculationTwoTotal:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul2[0].Calcul[0].Amount2[0].Amount[0]',
	rebateApplicationCalculationThreeAmount:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul3[0].Calcul[0].Amount1[0].Amount[0]',
	rebateApplicationCalculationThreeTotal:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul3[0].Calcul[0].Amount2[0].Amount[0]',
	rebateApplicationCalculationFourAmount:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul4[0].Calcul[0].Amount1[0].Amount[0]',
	rebateApplicationCalculationFourTotal:
		'form1[0].Page6[0].Section3_Continued[0].Line11[0].Line11_Text[0].Calcul4[0].Calcul[0].Amount2[0].Amount[0]',
	rebateApplicationCalculationFiveAmount:
		'form1[0].Page6[0].Calcul5[0].Calcul[0].Amount1[0].Amount[0]',
	rebateApplicationCalculationFiveTotal:
		'form1[0].Page6[0].Calcul5[0].Calcul[0].Amount2[0].Amount[0]',
	rebateApplicationAmount: 'form1[0].Page6[0].Amount[0]',
};

export interface ON_GST_190_CALCULATION_WS {
	applicationType: string;
	calculationType: string;
	rebateType1AOr2GSTPaid: string;
	rebateType1AOr2PurchasePrice: string;
	rebateType1AOr2AmountOne: string;
	rebateType1AOr2AmountTwo: string;
	rebateType1AOr2AmountThee: string;
	rebateType1AOr2CalculationOne: string;
	rebateType1AOr2AmountFour: string;
	rebateType1AOr2AmountFive: string;
	rebateType1AOr2CalculationTwo: string;
	rebateType1BOr5TotalPurchasePrice: string;
	rebateType1BOr5FairMarketValue: string;
	rebateType1BOr5AmountOne: string;
	rebateType1BOr5RebateRate: string;
	rebateType1BOr5CalculationOne: string;
	rebateType1BOr5AmountTwo: string;
	rebateType1BOr5AmountTwoCalculation: string;
	rebateType1BOr5AmountThree: string;
	rebateType1BOr5AmountThreeCalculation: string;
	rebateType1BOr5AmountFour: string;
	rebateType1BOr5AmountFourCalculation: string;
	rebateType1BOr5AmountFive: string;
	rebateType1BOr5AmountFiveCalculation: string;
	rebateType1BOr5CalculationTwo: string;
	rebateType3CalculationOneAmount: string;
	rebateType3CalculationOneRebateRate: string;
	rebateType3CalculationOneTotal: string;
	rebateType3CalculationTwoTotal: string;
	rebateApplicationCalculationOneAmount: string;
	rebateApplicationCalculationOneTotal: string;
	rebateApplicationCalculationTwoAmount: string;
	rebateApplicationCalculationTwoTotal: string;
	rebateApplicationCalculationThreeAmount: string;
	rebateApplicationCalculationThreeTotal: string;
	rebateApplicationCalculationFourAmount: string;
	rebateApplicationCalculationFourTotal: string;
	rebateApplicationCalculationFiveAmount: string;
	rebateApplicationCalculationFiveTotal: string;
	rebateApplicationAmount: string;
}
